import type { UIAlertProps } from '@core/types'
import type { ContentBlockRendererProps } from '@nimbus/types'
import type { AuthPath, AuthContainerData } from '#types'
import type { Merge } from 'type-fest'

export const useAuthContainer = createSharedComposable(() => {
    const route = useRoute()
    const reload = useReload()

    const { registrationState } = useWebauthn()

    const authInQuery = computed(() => {
        return !!route.query.auth
    })
    const authPath = computed(() => route.query.auth as AuthPath)

    // The signup step's title, shown as the sheet's heading (useSignupTitle).
    const signupTitle = ref<string>()

    // Promotion will be set from the view
    const data = ref<AuthContainerData>({
        selectedPromotion: undefined,
        promotionAccepted: false,
        email: '',
        password: '',
        accountExistDialogProps: {
            type: undefined,
            site: undefined
        },
    })

    const [pending, togglePending] = useToggle(false)

    // Must match the brand's signup form: on the last step the pending loader reads "Creating player account".
    const { signup } = useUkConfig()
    const { currentPage: step, isLastPage: isLastStep, prev: _prev, next: _next } = useOffsetPagination({
        total: signup?.steps ?? 4,
        page: 1,
        pageSize: 1,
    })
    const openedSteps = ref<number[]>([])
    const prev = () => {
        _prev()
        return navigateTo({ query: { auth: 'signup', step: step.value } })
    }
    const next = () => {
        _next()
        return navigateTo({ query: { auth: 'signup', step: step.value } })
    }

    const alert = ref<Merge<UIAlertProps, { message: string | ContentBlockRendererProps['content'] }>>()
    function setAlert(value?: typeof alert.value) {
        alert.value = value
    }

    function toggleDialog(state?: AuthPath | boolean) {
        if (state || !authInQuery.value) {
            return navigateTo({ query: { ...route.query, auth: (state || 'signup') as string } })
        }

        return navigateTo({
            query: {
                ...route.query,
                auth: undefined,
                step: undefined
            }
        })
    }

    async function complete(redirect?: string) {
        await toggleDialog(false)

        await reload(redirect)
    }

    watch(() => registrationState.value.loading, loading => togglePending(loading))

    return {
        data,
        authInQuery,
        authPath,
        complete,
        // Pending
        pending: readonly(pending),
        togglePending,
        // Alert
        alert: readonly(alert),
        setAlert,
        toggleDialog,
        signupTitle,
        // Steps
        openedSteps,
        step,
        isLastStep,
        prev,
        next,
    }
})
